import { useEffect, useImperativeHandle, useState } from 'react'
import {
  DropDownList,
  DropDownListChangeEvent
} from '@progress/kendo-react-dropdowns'
import { TextArea } from '@progress/kendo-react-inputs'
import { useAppSelector } from '../../stores'
import { DateInfo, PutDateReq } from '@gateway.Home'
import { withRefFC } from '../../components/hooks/withRefFC'
import { useLeaveGuard } from '../../components/hooks'
import { ErrorMessage, ErrorMessages } from '../../components/parts'
import { GetDateGateway } from '../../gateway/home/GetDateGateway'
import { CodeName } from '../../services/initializing/CodeName'
import Constants from '../../data/Constants.json'
import './DateSetting.css'
import { Authority } from '../../gateway/system/Authority'

export interface DateSettingHandler {
  // 画面で更新されたデータを取得する
  getUpdatedDate: () => PutDateReq | undefined
  fetchDate: () => void
}
export const DateSetting = withRefFC<DateSettingHandler>((ref) => {
  // 日付区分ドロップダウンで選択した値
  const [dateKbn, setDateKbn] = useState<CodeName>()
  // 年ドロップダウンで選択した値
  const [yearValue, setYearValue] = useState('')
  // 月ドロップダウンで選択した値
  const [monthValue, setMonthValue] = useState('')
  // 日ドロップダウンで選択した値
  const [dayValue, setDayValue] = useState('')
  // テキスト
  const [text, setText] = useState('')
  // 現在の日付区分の日付情報リスト
  const [dateList, setDateList] = useState<DateInfo[]>([])

  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  useLeaveGuard([text], '内容が編集中です。このページを離れますか？')

  const common = useAppSelector((x) => x.common)
  const userProp = useAppSelector((x) => x.common.userProp)

  // 日付区分のドロップダウン項目
  const dateKbnList = common.initialData.codeNames('DE01')
  // 年のドロップダウン項目（現在の年を基準に+3年までの4年間）
  const yearList = Array.from({ length: 4 }).map((_, k) => k + new Date().getFullYear())
  // 月のドロップダウン項目（1月～12月の並び）
  const monthList = Constants.month.map(m => Number(m)).sort((a, b) => a - b).map(m => m.toString())

  useImperativeHandle(ref, () => ({
    getUpdatedDate,
    fetchDate
  }))

  /**
   * 日付区分の変更
   */
  const handleSelectDateKbn = (event: DropDownListChangeEvent) => {
    setDateKbn(event.value)
  }

  /**
   * 年の変更
   */
  const handleSelectYear = (event: DropDownListChangeEvent) => {
    setYearValue(event.value)
  }

  /**
   * 月の変更
   */
  const handleSelectMonth = (event: DropDownListChangeEvent) => {
    setMonthValue(event.value)
  }

  /**
   * 日の変更
   */
  const handleSelectDay = (event: DropDownListChangeEvent) => {
    setDayValue(event.value)
  }

  const getUpdatedDate = () => {
    if (dateKbn !== undefined && yearValue !== '' && monthValue !== '' && dayValue !== '') {
      const year = yearValue.toString()
      const month = Number(monthValue) > 9 ? monthValue : `0${monthValue}`
      const day = Number(dayValue) > 9 ? dayValue : `0${dayValue}`
      const date = `${year}-${month}-${day}T00:00:00.0000000`
      const findedDate = dateList.find(d => d.year === year && d.month === month)

      const updatedDate: PutDateReq = {
        dateKbn: dateKbn.CODE ?? '',
        date,
        year,
        month,
        updDtm: findedDate?.updDtm,
        beforeDate: findedDate?.date
      }
      return updatedDate
    }

    // 入力情報が全て揃っていない場合
    return undefined
  }

  const fetchDate = () => {
    if (dateKbn !== undefined) {
      // 当月以降の日付情報を取得するためDateFromを生成
      //   dateFrom: yyyy-MM-01T00:00:00.000000
      const date = new Date()
      const year = date.getFullYear()
      const month = date.getMonth() + 1 > 9 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`
      const dateFrom = `${year}-${month}-01T00:00:00.0000000`

      GetDateGateway.getDate({ dateKbn: dateKbn?.CODE ?? '', dateFrom })
        .then((result) => {
          if (!result.error) {
            setDateList(result.data.dateList)
          } else {
            if (Array.isArray(result.messages) && result.messages.length > 0) {
              const errorMessages = result.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {})
    }
  }

  /**
   *  初期値の設定
   */
  useEffect(() => {
    setDateKbn(dateKbnList[0])
  }, [])

  /**
   * 日付区分が変化した場合
   *  - 該当の日付区分の日付情報を取得
   *  - 年／月／日を初期化
   */
  useEffect(() => {
    fetchDate()
    setYearValue('')
    setMonthValue('')
    setDayValue('')
  }, [dateKbn])

  /**
   * 日付情報リストが変化した場合
   *  - 取得済みの日付情報リストの一覧をテキストに設定
   */
  useEffect(() => {
    const text = dateList.map(d => new Date(d.date).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })).join('\n')
    setText(text)
  }, [dateList])

  return (
    <>
      <DropDownList
        style={{ marginBottom: 10, width: '25%' }}
        onChange={handleSelectDateKbn}
        data={dateKbnList}
        textField="NAME"
        dataItemKey="CODE"
        value={dateKbn}
        popupSettings={{ className: 'pcy-dropdownlist' }}
      />
      <DropDownList
        style={{ marginBottom: 10, marginLeft: 30, width: '20%' }}
        onChange={handleSelectYear}
        data={yearList}
        value={yearValue}
        popupSettings={{ className: 'pcy-dropdownlist' }}
        disabled={userProp?.authority !== Authority.Builder}
      />
      <DropDownList
        style={{ marginBottom: 10, width: '20%' }}
        onChange={handleSelectMonth}
        data={monthList}
        value={monthValue}
        popupSettings={{ className: 'pcy-dropdownlist' }}
        disabled={userProp?.authority !== Authority.Builder}
      />
      <DropDownList
        style={{ marginBottom: 10, width: '20%' }}
        onChange={handleSelectDay}
        data={Constants.day}
        value={dayValue}
        popupSettings={{ className: 'pcy-dropdownlist' }}
        disabled={userProp?.authority !== Authority.Builder}
      />
      <TextArea rows={4} value={text} readOnly />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
})
